import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { LoadingScreen } from '@bala/ui';
import { useAuth } from './auth';
import { RequireAccess } from './components/RequireAccess';
import { Shell } from './components/Shell';
import { LoginPage } from './pages/Login';
import { InvitationPage, PasswordResetPage } from './pages/Invitation';
import { BootstrapPage } from './pages/Bootstrap';

/*
 * Découpage en modules chargés à la demande : l'écran d'accueil s'affiche sans
 * attendre le code des finances ou du calendrier. Les onglets rarement ouverts
 * ne pèsent pas sur le démarrage, ce qui compte sur un téléphone en 4G.
 */
const HomePage = lazy(() => import('./pages/Home'));
const ListPage = lazy(() => import('./pages/People'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const ActivityPage = lazy(() => import('./pages/Activity'));
const AttendancePage = lazy(() => import('./pages/Attendance'));
const TasksPage = lazy(() => import('./pages/Tasks'));
const FinancePage = lazy(() => import('./pages/Finance'));
const GamesPage = lazy(() => import('./pages/Games'));
const InventoryPage = lazy(() => import('./pages/Inventory'));
const UniformesPage = lazy(() => import('./pages/Uniformes'));
const PhotosPage = lazy(() => import('./pages/Photos'));
const LostItemsPage = lazy(() => import('./pages/LostItems'));
const DocumentsPage = lazy(() => import('./pages/Documents'));
const MessagesPage = lazy(() => import('./pages/Messages'));
const BaygonPage = lazy(() => import('./pages/Baygon'));
const DevPage = lazy(() => import('./pages/Dev'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const MyProfilePage = lazy(() => import('./pages/MyProfile'));

/** Ancienne orthographe « Baigon » : redirige en conservant l'onglet. */
function RedirectLegacyBaygonTab() {
  const { tab } = useParams<{ tab?: string }>();
  return <Navigate to={tab ? `/baygon/${tab}` : '/baygon'} replace />;
}

export function App() {
  const auth = useAuth();

  if (auth.status === 'loading') {
    return <LoadingScreen message="Ouverture de Bala'App…" />;
  }

  if (auth.status === 'anonymous') {
    return (
      <Routes>
        {/* Les liens d'invitation et de réinitialisation doivent rester
            accessibles sans session. */}
        <Route path="/invitation/:token" element={<InvitationPage />} />
        <Route path="/reinitialisation/:token" element={<PasswordResetPage />} />
        {auth.needsBootstrap && <Route path="/installation" element={<BootstrapPage />} />}
        <Route path="*" element={auth.needsBootstrap ? <Navigate to="/installation" replace /> : <LoginPage />} />
      </Routes>
    );
  }

  return (
    <Shell>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/liste"
            element={
              <RequireAccess anyOf={['baladins', 'animateurs']}>
                <ListPage />
              </RequireAccess>
            }
          />
          <Route
            path="/liste/:tab"
            element={
              <RequireAccess anyOf={['baladins', 'animateurs']}>
                <ListPage />
              </RequireAccess>
            }
          />
          <Route
            path="/calendrier"
            element={
              <RequireAccess resource="calendar">
                <CalendarPage />
              </RequireAccess>
            }
          />
          <Route
            path="/calendrier/:id"
            element={
              <RequireAccess resource="calendar">
                <ActivityPage />
              </RequireAccess>
            }
          />
          <Route
            path="/presences"
            element={
              <RequireAccess resource="attendance">
                <AttendancePage />
              </RequireAccess>
            }
          />
          <Route
            path="/taches"
            element={
              <RequireAccess resource="tasks">
                <TasksPage />
              </RequireAccess>
            }
          />
          <Route
            path="/taches/:id"
            element={
              <RequireAccess resource="tasks">
                <TasksPage />
              </RequireAccess>
            }
          />
          <Route
            path="/finances"
            element={
              <RequireAccess resource="finances">
                <FinancePage />
              </RequireAccess>
            }
          />
          <Route
            path="/jeux"
            element={
              <RequireAccess resource="games">
                <GamesPage />
              </RequireAccess>
            }
          />
          <Route
            path="/jeux/:id"
            element={
              <RequireAccess resource="games">
                <GamesPage />
              </RequireAccess>
            }
          />
          <Route
            path="/inventaire"
            element={
              <RequireAccess resource="inventaire">
                <InventoryPage />
              </RequireAccess>
            }
          />
          <Route
            path="/uniformes"
            element={
              <RequireAccess resource="uniformes">
                <UniformesPage />
              </RequireAccess>
            }
          />
          <Route
            path="/photos"
            element={
              <RequireAccess resource="photos">
                <PhotosPage />
              </RequireAccess>
            }
          />
          <Route
            path="/photos/:id"
            element={
              <RequireAccess resource="photos">
                <PhotosPage />
              </RequireAccess>
            }
          />
          <Route
            path="/objets-perdus"
            element={
              <RequireAccess resource="photos">
                <LostItemsPage />
              </RequireAccess>
            }
          />
          <Route
            path="/documents"
            element={
              <RequireAccess resource="documents">
                <DocumentsPage />
              </RequireAccess>
            }
          />
          <Route
            path="/communications"
            element={
              <RequireAccess resource="messages">
                <MessagesPage />
              </RequireAccess>
            }
          />
          <Route
            path="/communications/:id"
            element={
              <RequireAccess resource="messages">
                <MessagesPage />
              </RequireAccess>
            }
          />
          <Route
            path="/baygon"
            element={
              <RequireAccess resource="admin">
                <BaygonPage />
              </RequireAccess>
            }
          />
          <Route
            path="/baygon/:tab"
            element={
              <RequireAccess resource="admin">
                <BaygonPage />
              </RequireAccess>
            }
          />
          <Route path="/contacts-parents" element={<Navigate to="/baygon/contacts" replace />} />
          <Route
            path="/dev"
            element={
              <RequireAccess resource="admin">
                <DevPage />
              </RequireAccess>
            }
          />
          <Route
            path="/dev/:tab"
            element={
              <RequireAccess resource="admin">
                <DevPage />
              </RequireAccess>
            }
          />
          {/* Ancienne orthographe « Baigon » : conserver les favoris. */}
          <Route path="/baigon" element={<Navigate to="/baygon" replace />} />
          <Route path="/baigon/:tab" element={<RedirectLegacyBaygonTab />} />
          <Route path="/parametres" element={<SettingsPage />} />
          {/* Fiche personnelle : sans droit d'écriture sur la Liste. */}
          <Route path="/ma-fiche" element={<MyProfilePage />} />
          {/* Une URL inconnue (ancien favori, lien partagé) ramène à l'accueil
              plutôt que d'afficher une page d'erreur. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Shell>
  );
}
